/**
 * Custom hook for managing print functionality
 */

import { useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import type { Dochadzka, PrintSettings } from "../types";
import { formatTime } from "../utils";

export function useAttendancePrint() {
  const { t, locale } = useTranslation();

  const [printSettings, setPrintSettings] = useState<PrintSettings>({
    showDate: true,
    showTime: true,
    showClient: true,
    showDuration: true,
    showTrainingType: true,
    showPrice: true,
    showNotes: false,
    showTotals: true,
    includeDateRange: true,
  });

  const toggleAllColumns = (checked: boolean) => {
    setPrintSettings({
      showDate: checked,
      showTime: checked,
      showClient: checked,
      showDuration: checked,
      showTrainingType: checked,
      showPrice: checked,
      showNotes: checked,
      showTotals: checked,
      includeDateRange: checked,
    });
  };

  const areAllColumnsSelected =
    printSettings.showDate &&
    printSettings.showTime &&
    printSettings.showClient &&
    printSettings.showDuration &&
    printSettings.showTrainingType &&
    printSettings.showPrice &&
    printSettings.showNotes &&
    printSettings.showTotals &&
    printSettings.includeDateRange;

  const generatePrintHtml = (
    dataToPrint: Dochadzka[],
    clientInfo: string,
    startDate: string,
    endDate: string,
    forAllClients: boolean,
    klienti?: any[]
  ): string => {
    // Calculate totals
    const printTotalSessions = dataToPrint.length;
    const printTotalMinutes = dataToPrint.reduce(
      (sum, d) => sum + (d.dlzkaTreningu || 0),
      0
    );
    const hasIncomeBreakdown = dataToPrint.some(
      session => session.income !== undefined
    );
    const printTotalRevenue = dataToPrint.reduce(
      (sum, d) => sum + (d.income?.selectedGross ?? parseFloat(d.cena || "0")),
      0
    );
    const printTotalProfit = hasIncomeBreakdown
      ? dataToPrint.reduce((sum, d) => sum + (d.income?.selectedProfit ?? 0), 0)
      : null;

    const dateRange = `${new Date(startDate).toLocaleDateString(locale)} - ${new Date(endDate).toLocaleDateString(locale)}`;

    // Get title based on context
    const printTitle = forAllClients
      ? t("attendance:print.titleAllClients")
      : t("attendance:print.titleSingleClient");

    const printSubtitle = forAllClients
      ? t("attendance:print.subtitleAllClients")
      : t("attendance:print.subtitleSingleClient");

    const totalRevenueLabel = forAllClients
      ? t("attendance:print.totalRevenueAll")
      : t("attendance:print.totalRevenueSingle");
    const displayedRevenueLabel = hasIncomeBreakdown
      ? t("attendance:summary.selectedIncome")
      : totalRevenueLabel;

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${printTitle}</title>
        <style>
          @media print {
            @page {
              margin: 15mm;
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 11px;
              color: #000;
              line-height: 1.3;
            }
          }
          body {
            font-family: Arial, sans-serif;
            font-size: 11px;
            color: #000;
            line-height: 1.3;
            margin: 0;
            padding: 15px;
          }
          .print-header {
            text-align: center;
            margin-bottom: 15px;
            border-bottom: 1px solid #000;
            padding-bottom: 8px;
          }
          .print-title {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 3px;
          }
          .print-subtitle {
            font-size: 12px;
            color: #666;
            margin-bottom: 8px;
          }
          .print-info {
            display: flex;
            justify-content: space-between;
            margin-bottom: 15px;
            font-size: 10px;
            background-color: #f8f9fa;
            padding: 8px;
            border-radius: 4px;
          }
          .print-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 15px;
            font-size: 10px;
          }
          .print-table th {
            background-color: #e9ecef;
            border: 1px solid #dee2e6;
            padding: 6px;
            text-align: left;
            font-weight: bold;
            white-space: nowrap;
          }
          .print-table td {
            border: 1px solid #dee2e6;
            padding: 6px;
            vertical-align: top;
          }
          .print-totals {
            margin-top: 20px;
            padding-top: 10px;
            border-top: 2px solid #000;
            font-size: 11px;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 4px;
          }
          .total-label {
            font-weight: bold;
          }
          .total-value {
            text-align: right;
            min-width: 100px;
          }
          .print-footer {
            margin-top: 20px;
            font-size: 9px;
            color: #666;
            text-align: center;
            border-top: 1px solid #dee2e6;
            padding-top: 8px;
          }
          .text-right {
            text-align: right;
          }
          .text-center {
            text-align: center;
          }
          .services-cell {
            max-width: 250px;
            word-wrap: break-word;
          }
        </style>
      </head>
      <body>
        <div class="print-header">
          <div class="print-title">${printTitle}</div>
          <div class="print-subtitle">${printSubtitle}</div>
        </div>
        
        <div class="print-info">
          <div>
            <strong>${t("attendance:print.client")}:</strong> ${clientInfo}<br>
            ${printSettings.includeDateRange ? `<strong>${t("attendance:print.dateRange")}:</strong> ${dateRange}<br>` : ""}
            <strong>${t("attendance:print.reportDate")}:</strong> ${new Date().toLocaleDateString(locale)}
          </div>
          <div style="text-align: right;">
            <strong>${t("attendance:print.totalSessions")}:</strong> ${printTotalSessions}<br>
            <strong>${t("attendance:print.totalHours")}:</strong> ${Math.floor((printTotalMinutes || 0) / 60)}h ${(printTotalMinutes || 0) % 60}m<br>
            <strong>${displayedRevenueLabel}:</strong> €${(printTotalRevenue || 0).toFixed(2)}
            ${printTotalProfit !== null ? `<br><strong>${t("attendance:summary.selectedProfit")}:</strong> €${printTotalProfit.toFixed(2)}` : ""}
          </div>
        </div>
        
        <table class="print-table">
          <thead>
            <tr>
              ${printSettings.showDate ? `<th>${t("attendance:print.date")}</th>` : ""}
              ${printSettings.showTime ? `<th>${t("attendance:print.time")}</th>` : ""}
              ${printSettings.showClient && forAllClients ? `<th>${t("attendance:print.clientName")}</th>` : ""}
              ${printSettings.showDuration ? `<th>${t("attendance:print.duration")}</th>` : ""}
              ${printSettings.showTrainingType ? `<th class="services-cell">${t("attendance:print.services")}</th>` : ""}
              ${printSettings.showPrice ? `<th>${t("attendance:print.price")}</th>` : ""}
              ${printSettings.showNotes ? `<th>${t("attendance:print.notes")}</th>` : ""}
            </tr>
          </thead>
          <tbody>
            ${dataToPrint
              .map(session => {
                const clientDisplay =
                  session.klientAlias ||
                  klienti?.find(k => k.id === session.klientId)?.alias ||
                  `${session.klientMeno || ""} ${session.klientPriezvisko || ""}`.trim();

                // Get services - use formattedServices if available, otherwise fallback to typTreningu
                const services =
                  session.formattedServices ||
                  session.typTreningu?.join(", ") ||
                  t("attendance:print.noServices");

                return `
                <tr>
                  ${printSettings.showDate ? `<td>${new Date(session.datum).toLocaleDateString(locale)}</td>` : ""}
                  ${printSettings.showTime ? `<td class="text-center">${formatTime(session.cas)}</td>` : ""}
                  ${printSettings.showClient && forAllClients ? `<td>${clientDisplay}</td>` : ""}
                  ${printSettings.showDuration ? `<td class="text-center">${session.dlzkaTreningu}</td>` : ""}
                  ${printSettings.showTrainingType ? `<td class="services-cell">${services}</td>` : ""}
                  ${printSettings.showPrice ? `<td class="text-right">${(session.income?.selectedGross ?? Number(session.cena || "0")).toFixed(2)}</td>` : ""}
                  ${printSettings.showNotes ? `<td>${session.poznamka || t("attendance:print.noNotes")}</td>` : ""}
                </tr>
              `;
              })
              .join("")}
          </tbody>
        </table>
        
        ${
          printSettings.showTotals
            ? `
          <div class="print-totals">
            <div class="total-row">
              <span class="total-label">${t("attendance:print.totalSessions")}:</span>
              <span class="total-value">${printTotalSessions}</span>
            </div>
            <div class="total-row">
              <span class="total-label">${t("attendance:print.totalHours")}:</span>
              <span class="total-value">${Math.floor((printTotalMinutes || 0) / 60)}h ${(printTotalMinutes || 0) % 60}m</span>
            </div>
            <div class="total-row">
              <span class="total-label">${displayedRevenueLabel}:</span>
              <span class="total-value">€${(printTotalRevenue || 0).toFixed(2)}</span>
            </div>
            ${
              printTotalProfit !== null
                ? `<div class="total-row">
              <span class="total-label">${t("attendance:summary.selectedProfit")}:</span>
              <span class="total-value">€${printTotalProfit.toFixed(2)}</span>
            </div>`
                : ""
            }
          </div>
        `
            : ""
        }
        
        <div class="print-footer">
          ${t("attendance:print.generatedOn")} ${new Date().toLocaleString(locale)}
        </div>
        
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.onafterprint = function() {
                setTimeout(function() {
                  window.close();
                }, 100);
              };
            }, 250);
          };
        </script>
      </body>
      </html>
    `;
  };

  const handlePrint = (
    dataToPrint: Dochadzka[],
    clientInfo: string,
    startDate: string,
    endDate: string,
    forAllClients: boolean,
    klienti?: any[]
  ) => {
    if (dataToPrint.length === 0) {
      toast.error(t("attendance:print.noDataForPrint"));
      return;
    }

    const printWindow = window.open("", "_blank", "width=900,height=600");
    if (!printWindow) {
      toast.error(t("attendance:print.popupError"));
      return;
    }

    const printHtml = generatePrintHtml(
      dataToPrint,
      clientInfo,
      startDate,
      endDate,
      forAllClients,
      klienti
    );

    printWindow.document.write(printHtml);
    printWindow.document.close();
  };

  return {
    printSettings,
    setPrintSettings,
    toggleAllColumns,
    areAllColumnsSelected,
    handlePrint,
  };
}
